import { IViolationResult } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { Camera } from "../../../SharedGL/Camera";
import { FullDayBuffer } from "./FullDayBuffer";
import { PairingBuffer } from "./shaders/PairingBuffer";
import { PairingShader } from "./shaders/PairingShader";
import { TextureShader } from "./shaders/TextureShader";

export interface IBufferUpdate {
  positions: number[];
  colors: number[];
}
export abstract class CrewScheduleErrorsGL {
  /** the start of the row to start rendering webGL */
  public static yStart = 0;
  /** the height of the crew schedule error box */
  public static yHeight = 6;
  /** when the buffers were last updated */
  public lastModified = 0;

  protected buffer: PairingBuffer;
  protected vertexCount = 0;
  protected violationDaysVertexCount = 0;

  protected violationsDayGL: FullDayBuffer;
  protected height = 0;

  /**
   * Creates instance of CrewScheduleErrorsGL
   * @param gl - WebGL rendering context
   * @param shader - the webGL shader to render the crew error
   * @param textureShader - the webGL Shader to render error days "X" repeating texture
   * @param texture - the webGL texture containing the repeating "X" texture
   */
  constructor(gl: WebGLRenderingContext, shader: PairingShader, textureShader: TextureShader, texture: WebGLTexture) {
    this.buffer = new PairingBuffer(gl, shader);
    this.violationsDayGL = new FullDayBuffer(gl, textureShader, texture);
  }

  /**
   * Draw the thin “top strip” overlay for a crew row.
   *
   * Renders at z = -1 so it appears above row backgrounds/patterns (pairings, NFAs, X texture)
   * but below text and outline overlays. Does nothing if the buffer is empty.
   *
   * @param gl - WebGL rendering context
   * @param camera - Shared camera providing the projection matrix
   * @param yStart - Row’s Y start in world coordinates
   */
  public drawTopStrip(gl: WebGLRenderingContext, camera: Camera, yStart: number): void {
    if (this.vertexCount > 0) {
      const offset = 0;
      this.buffer.draw(gl, camera, yStart, -1, offset, this.vertexCount);
    }
  }

  protected updateBuffers(
    gl: WebGLRenderingContext,
    violations: IViolationResult[],
    rowHeight: number,
    color: number[]
  ): void {
    const pairingPositions: number[] = [];
    const pairingColors: number[] = [];

    const violationDaysPositions: number[] = [];
    const violationDaysColors: number[] = [];

    // iterating over an aircraft (row) so height will always be same
    const y0 = CrewScheduleErrorsGL.yStart;
    const y1 = y0 + CrewScheduleErrorsGL.yHeight;

    const selectedDayY0 = y1 + 1;
    const selectedDayY1 = y1 + rowHeight - CrewScheduleErrorsGL.yHeight - 2;

    // const color = [1.0, 0.0, 0.0, 1.0];
    const [r, g, b, a] = color;
    const selectedDayColor = [r, g, b, a * 0.5]; //[74 / 255, 20 / 255, 140 / 255, 1.0];
    violations.forEach((violation: IViolationResult) => {
      if (violation.status !== "fail" || !violation.period || violation.noShow) {
        return;
      }
      // this.violationVertextOffset[violation.link] = {
      //   offset: pairingPositions.length / 2,
      //   vertexCount: 0,
      // };
      const x0 = new Date(violation.period.start).getTime() - Camera.referenceTime;
      const x1 = new Date(violation.period.end).getTime() - Camera.referenceTime;
      const positions = [x0, y0, x1, y0, x1, y1, x0, y0, x1, y1, x0, y1];
      pairingPositions.push(...positions);
      pairingColors.push(...color, ...color, ...color, ...color, ...color, ...color);
    });

    this.buffer.updateBuffers(gl, pairingPositions, pairingColors);
    this.vertexCount = pairingPositions.length / 2;

    this.violationsDayGL.updateBuffers(gl, [], []);
    this.violationDaysVertexCount = 0;

    this.lastModified = Date.now();
  }

  /**
   * draw - draws the basic crew error violation
   * @param gl - The WebGL rendering context
   * @param camera - the camera to get dimensions of visible window
   * @param yStart - the row start y dimension
   * @param z - the z-index where to draw. This determines which objects will overlap this or go behind
   */
  public draw(gl: WebGLRenderingContext, camera: Camera, yStart: number, z: number): void {
    if (this.vertexCount === 0) {
      return;
    }
    const [minX, maxX] = camera.getXRange();

    const offset = 0;
    if (this.vertexCount > 0) {
      this.buffer.draw(gl, camera, yStart, -1, offset, this.vertexCount);
    }

    if (this.violationDaysVertexCount > 0) {
      const xScale = gl.canvas.width / (maxX - Camera.referenceTime - (minX - Camera.referenceTime));
      const scale = [xScale, 0.01666];
      this.violationsDayGL.draw(gl, camera, yStart, 0, this.violationDaysVertexCount, scale, z);
    }
  }
}
